/**
 * src/utils/haptics.ts — tiny haptic feedback helper.
 *
 * Wraps navigator.vibrate with named patterns and a reduced-motion guard.
 * Works in the Android WebView; iOS WKWebView ignores vibrate() (graceful
 * no-op) — upgrade to @capacitor/haptics later for true iOS haptics. Centralised
 * so callers don't re-implement the try/guard each time.
 */

function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

function buzz(pattern: number | number[]): void {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  if (prefersReducedMotion()) return;
  try { navigator.vibrate(pattern); } catch { /* non-fatal — unsupported context */ }
}

export const haptics = {
  /** Light confirmation tap (button press, toggle). */
  tap: () => buzz(12),
  /** Positive outcome (scheduled, saved, "tucked in"). */
  success: () => buzz([0, 16, 48, 26]),
  /** Something needs attention (error, validation). */
  warn: () => buzz([0, 32, 44, 32]),
};
